import { EscapeSQLString } from '@memberjunction/global';

/**
 * Search shaping for Channel Messages, shared by every caller (Review & Approve today; any
 * server action, agent tool or other screen tomorrow). Pure: no metadata, no provider, no I/O.
 *
 * Why one function: MJ's provider turns a RunView `UserSearchString` into
 * `ID IN (SELECT ID FROM Izzy.fnSearchChannel_Messages('<term>'))` when the entity has full-text
 * search enabled, and the term is passed through as-is once it contains a standalone AND (the
 * provider's own stop-word stripping is skipped for pre-shaped input). Two callers with their own
 * shaping would get different results from the same index, so the shaping lives here, with its
 * tests, and the UI only calls it.
 *
 * Matching model (Outlook's): every word must match as a prefix, in any order, across sender,
 * subject, recipients and the plain-text body; a fully quoted input is an exact phrase;
 * `from:` / `subject:` / `to:` stay substring matches on their columns. Stop words (SQL Server's
 * English system list) are dropped so a term never fails for containing "the".
 *
 * Design: docs/message-search-fulltext-design.md section 3.4.
 */

/** SQL Server English system stop words (`sys.fulltext_system_stopwords`, language 1033), lower-cased. */
export const CHANNEL_MESSAGE_SEARCH_STOP_WORDS: ReadonlySet<string> = new Set([
    '$', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
    'a', 'about', 'after', 'all', 'also', 'an', 'and', 'another', 'any', 'are', 'as', 'at',
    'b', 'be', 'because', 'been', 'before', 'being', 'between', 'both', 'but', 'by',
    'c', 'came', 'can', 'come', 'could',
    'd', 'did', 'do', 'does',
    'e', 'each', 'else',
    'f', 'for', 'from',
    'g', 'get', 'got',
    'h', 'had', 'has', 'have', 'he', 'her', 'here', 'him', 'himself', 'his', 'how',
    'i', 'if', 'in', 'into', 'is', 'it', 'its',
    'j', 'just',
    'k',
    'l', 'like',
    'm', 'make', 'many', 'me', 'might', 'more', 'most', 'much', 'must', 'my',
    'n', 'never', 'no', 'now',
    'o', 'of', 'on', 'only', 'or', 'other', 'our', 'out', 'over',
    'p', 'q',
    'r', 're',
    's', 'said', 'same', 'see', 'should', 'since', 'so', 'some', 'still', 'such',
    't', 'take', 'than', 'that', 'the', 'their', 'them', 'then', 'there', 'these', 'they', 'this',
    'those', 'through', 'to', 'too',
    'u', 'under', 'up', 'use',
    'v', 'very',
    'w', 'want', 'was', 'way', 'we', 'well', 'were', 'what', 'when', 'where', 'which', 'while',
    'who', 'will', 'with', 'would',
    'x', 'y', 'you', 'your', 'z',
]);

/** Width of the generated search function's `@searchTerm NVARCHAR(255)` parameter. */
export const CHANNEL_MESSAGE_SEARCH_TERM_MAX_CHARS = 255;

/** Operators the search box understands; each is a substring match on one column. */
export interface ChannelMessageSearchOperators {
    from?: string;
    subject?: string;
    to?: string;
}

export interface ChannelMessageSearchOptions {
    /**
     * True when the Channel Messages entity has `FullTextSearchEnabled` and a search function, so
     * the free text goes out as `UserSearchString`. False routes it through the `LIKE` fallback on
     * the plain-text column inside `extraFilter` (a database where the index is not live, or a
     * rollback).
     */
    fullTextAvailable: boolean;
}

export interface ChannelMessageSearch {
    /** Free-text words after cleaning and stop-word removal, in input order, de-duplicated. */
    words: string[];
    /** The exact phrase when the whole free text was one double-quoted token; otherwise null. */
    phrase: string | null;
    operators: ChannelMessageSearchOperators;
    /** The shaped term for RunView's `UserSearchString`, or undefined when there is nothing to send. */
    userSearchString: string | undefined;
    /** SQL for RunView's `ExtraFilter`: operator clauses, plus the fallback body clause when full-text is not available. Empty when nothing applies. */
    extraFilter: string;
    /** True when words were dropped to keep the term within the function's 255-character parameter. */
    truncated: boolean;
    /** True when the input carries no words, no phrase and no operator. */
    isEmpty: boolean;
}

const OPERATOR_NAMES = ['from', 'subject', 'to'] as const;
const OPERATOR_COLUMNS: Record<(typeof OPERATOR_NAMES)[number], string> = {
    from: 'Sender',
    subject: 'Subject',
    to: 'Recipient',
};
const FALLBACK_COLUMNS = ['Sender', 'Subject', 'Recipient', 'MessageContentPreview', 'MessageContentText'];

/**
 * ASCII folding for search, the step Lucene / Elasticsearch / Azure AI Search apply as `asciifolding`
 * on both the indexed text and the query: accented Latin letters lose their marks (café → cafe,
 * Müller → Muller) and typographic punctuation becomes its ASCII form (’ ‘ → ', “ ” → ", en/em
 * dashes → -, ellipsis → ..., no-break space → space). SQL Server has no query-time analyzer, so
 * the fold is applied once to the stored `MessageContentText` (save path and backfill) and once to
 * the typed words here; the English word breaker otherwise indexes "don’t" and "don't", or "café"
 * and "cafe", as different tokens (measured on stage 2026-10-22). Operator values (`from:`,
 * `subject:`, `to:`) are NOT folded: they become LIKE filters over the raw columns.
 */
export function FoldForSearch(text: string): string {
    return foldTypographicPunctuation(text)
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        // Invisible format characters (zero-width joiners, soft hyphens, bidi marks, BOM; Unicode
        // category Cf) sit inside words in HTML mail and would split tokens. ICU folding removes
        // these "default ignorable" code points; on stage 1014 ZWNJ and 139 soft hyphens survived
        // the accent/punctuation fold before this step.
        .replace(/\p{Cf}/gu, '');
}

/** The punctuation half of the fold: typographic quotes, dashes, ellipsis and no-break space to ASCII. */
function foldTypographicPunctuation(text: string): string {
    return text
        .replace(/[‘’‚‛′]/g, "'")
        .replace(/[“”„‟″]/g, '"')
        .replace(/[‐-―−]/g, '-')
        .replace(/…/g, '...')
        .replace(/ /g, ' ');
}

/**
 * Parse the search box text and build what a RunView needs.
 */
export function BuildChannelMessageSearch(raw: string | null | undefined, options: ChannelMessageSearchOptions): ChannelMessageSearch {
    // Punctuation is folded before tokenizing so a phrase typed with curly quotes (“annual report”,
    // what phones and Word auto-correct produce) is still recognized as a phrase; accents are folded
    // per free token afterwards, leaving operator values untouched for their LIKE filters.
    const { operators, freeTokens: rawTokens } = tokenize(foldTypographicPunctuation(raw ?? ''));
    const freeTokens = rawTokens.map(FoldForSearch);
    const phrase = wholeInputPhrase(freeTokens);
    const words = phrase === null ? cleanWords(freeTokens) : [];

    const term = options.fullTextAvailable ? buildFullTextTerm(words, phrase) : { value: undefined, truncated: false };
    const extraFilter = buildExtraFilter(operators, words, phrase, options.fullTextAvailable);

    return {
        words,
        phrase,
        operators,
        userSearchString: term.value,
        extraFilter,
        truncated: term.truncated,
        isEmpty: words.length === 0 && phrase === null && Object.keys(operators).length === 0,
    };
}

/**
 * Whether a field value satisfies the free-text part of a search, with the same model the index
 * applies: every word as a case-insensitive prefix of some token in the value, or the exact phrase
 * as a substring. Used for the match-type chips; operators are checked by the caller as substrings.
 */
export function ChannelMessageSearchMatches(value: string | null | undefined, search: ChannelMessageSearch): boolean {
    if (!value) return false;
    // The words are folded, so the compared value is folded the same way (café matches "cafe").
    const lower = FoldForSearch(value).toLowerCase();
    if (search.phrase !== null) {
        return lower.includes(search.phrase.toLowerCase());
    }
    if (search.words.length === 0) return false;
    const tokens = lower.split(/[^a-z0-9]+/).filter(t => t.length > 0);
    return search.words.every(word => {
        const w = word.toLowerCase();
        return tokens.some(t => t.startsWith(w));
    });
}

// ─── parsing ──────────────────────────────────────────────────────────────────

function tokenize(raw: string): { operators: ChannelMessageSearchOperators; freeTokens: string[] } {
    const operators: ChannelMessageSearchOperators = {};
    const freeTokens: string[] = [];
    // Double-quoted values stay one token: from:"jane doe", "annual report"
    const tokens = raw.trim().match(/(?:[a-z]+:)?"[^"]*"|\S+/gi) ?? [];
    for (const token of tokens) {
        const opMatch = token.match(/^(from|subject|to):(.*)$/i);
        if (opMatch) {
            const value = opMatch[2].replace(/^"|"$/g, '').trim();
            if (value) {
                operators[opMatch[1].toLowerCase() as (typeof OPERATOR_NAMES)[number]] = value;
            }
            continue;
        }
        freeTokens.push(token);
    }
    return { operators, freeTokens };
}

/** A phrase only when the entire free text is one double-quoted token with something inside. */
function wholeInputPhrase(freeTokens: string[]): string | null {
    if (freeTokens.length !== 1) return null;
    const token = freeTokens[0];
    if (!(token.startsWith('"') && token.endsWith('"') && token.length >= 2)) return null;
    const inner = token.slice(1, -1).replace(/["*]/g, '').replace(/\s+/g, ' ').trim();
    if (inner.length === 0) return null;
    // A phrase of nothing but stop words cannot match anything the index holds.
    const meaningful = inner.split(' ').some(w => !CHANNEL_MESSAGE_SEARCH_STOP_WORDS.has(w.toLowerCase()));
    return meaningful ? inner : null;
}

/** Split quoted and bare tokens into words, drop quotes and asterisks, stop words and duplicates. */
function cleanWords(freeTokens: string[]): string[] {
    const seen = new Set<string>();
    const words: string[] = [];
    for (const token of freeTokens) {
        for (const piece of token.split(/\s+/)) {
            const word = piece.replace(/["*]/g, '').trim();
            if (word.length === 0) continue;
            const key = word.toLowerCase();
            if (CHANNEL_MESSAGE_SEARCH_STOP_WORDS.has(key) || seen.has(key)) continue;
            seen.add(key);
            words.push(word);
        }
    }
    return words;
}

// ─── full-text term ───────────────────────────────────────────────────────────

/**
 * `"w1*" AND "w2*"` for words, `"the phrase"` for a phrase. Words are added until the next one
 * would push the term past the function's parameter width; the rest are dropped at that word
 * boundary and reported through `truncated`. Single quotes are left alone: the provider doubles
 * them when it splices the term into its SQL literal.
 */
function buildFullTextTerm(words: string[], phrase: string | null): { value: string | undefined; truncated: boolean } {
    if (phrase !== null) {
        const value = `"${phrase}"`;
        if (value.length <= CHANNEL_MESSAGE_SEARCH_TERM_MAX_CHARS) return { value, truncated: false };
        return { value: `"${phrase.slice(0, CHANNEL_MESSAGE_SEARCH_TERM_MAX_CHARS - 2).replace(/\s+\S*$/, '')}"`, truncated: true };
    }
    if (words.length === 0) return { value: undefined, truncated: false };

    const parts: string[] = [];
    let length = 0;
    let truncated = false;
    for (const word of words) {
        const part = `"${word}*"`;
        const next = length + part.length + (parts.length > 0 ? 5 : 0); // ' AND '
        if (next > CHANNEL_MESSAGE_SEARCH_TERM_MAX_CHARS) {
            truncated = true;
            break;
        }
        parts.push(part);
        length = next;
    }
    return { value: parts.length > 0 ? parts.join(' AND ') : undefined, truncated };
}

// ─── ExtraFilter SQL ──────────────────────────────────────────────────────────

/** Operator clauses always; the plain-text fallback for the free text only when full-text is not available. */
function buildExtraFilter(operators: ChannelMessageSearchOperators, words: string[], phrase: string | null, fullTextAvailable: boolean): string {
    const clauses: string[] = [];
    for (const name of OPERATOR_NAMES) {
        const value = operators[name];
        if (value) clauses.push(likeClause(OPERATOR_COLUMNS[name], value));
    }
    if (!fullTextAvailable) {
        if (phrase !== null) {
            clauses.push(anyColumnLike(phrase));
        } else {
            for (const word of words) clauses.push(anyColumnLike(word));
        }
    }
    return clauses.join(' AND ');
}

function anyColumnLike(value: string): string {
    return `(${FALLBACK_COLUMNS.map(column => likeClause(column, value)).join(' OR ')})`;
}

function likeClause(column: string, value: string): string {
    return `${column} LIKE '%${escapeLikeValue(value)}%'`;
}

/**
 * A value inside `LIKE '%...%'`: single quotes doubled (`EscapeSQLString`) and the wildcard
 * characters `[`, `%`, `_` bracketed so they match literally (SQL Server's escape-by-bracket form,
 * which needs no ESCAPE clause).
 */
function escapeLikeValue(value: string): string {
    return EscapeSQLString(value)
        .replace(/\[/g, '[[]')
        .replace(/%/g, '[%]')
        .replace(/_/g, '[_]');
}
